import * as Yup from 'yup';

import TextField from '@mui/material/TextField';

import { EMPTY_ARRAY } from '@e-socna/empty';
import { GroupingModeEnum } from '@e-socna/data-layer';

import { MemoConfirmDialog } from './MemoConfirmDialog';
import { suggestGroupLabel } from './utils/suggest-group-label.util';
import { DIALOG_AUTO_FOCUS_ATTRIBUTE } from '../../constants/dialog-auto-focus.constant';
import type { SubmitHandler } from './types/submit-handler.type';

export interface SwitchGroupingModeValues {
  readonly label: string;
}

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<SwitchGroupingModeValues>;
  readonly targetGroupingMode: GroupingModeEnum;
}

const DEFAULT_FIRST_GROUP_LABEL = suggestGroupLabel(EMPTY_ARRAY);

const texts = {
  [GroupingModeEnum.Manual]: {
    title: 'Découper',
    description:
      'Toutes les prestations seront placées dans un premier groupe, que vous pourrez ensuite compléter.',
    submitLabel: 'Découper',
    validationSchema: Yup.object().shape({
      label: Yup.string().trim().required('Libellé requis'),
    }),
  },
  [GroupingModeEnum.Singleton]: {
    title: 'Dégrouper',
    description:
      "Tous les groupes, leurs libellés et leurs totaux manuels seront supprimés. Leurs prestations seront fusionnées dans l'ordre des groupes, avec un total automatique.",
    submitLabel: 'Dégrouper',
    validationSchema: undefined,
  },
} as const;

export const SwitchGroupingModeForm = ({
  askMemoBeforeSubmit,
  isDialogOpen,
  onCloseDialog,
  onSubmit,
  targetGroupingMode,
}: Readonly<Props>) => {
  const { description, submitLabel, title, validationSchema } =
    texts[targetGroupingMode];

  return (
    <MemoConfirmDialog<SwitchGroupingModeValues>
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      autoFocusSubmit={targetGroupingMode !== GroupingModeEnum.Manual}
      description={description}
      id="switch-grouping-mode"
      initialValues={{ label: DEFAULT_FIRST_GROUP_LABEL }}
      isDialogOpen={isDialogOpen}
      onCloseDialog={onCloseDialog}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
      title={title}
      validationSchema={validationSchema}
    >
      {({ errors, handleBlur, handleChange, touched, values }) => {
        if (targetGroupingMode !== GroupingModeEnum.Manual) {
          return null;
        }

        return (
          <TextField
            error={!!(touched.label && errors.label)}
            fullWidth
            helperText={touched.label ? errors.label : undefined}
            id="switch-grouping-mode-label"
            label="Libellé du premier groupe"
            margin="normal"
            name="label"
            onBlur={handleBlur}
            onChange={handleChange}
            required
            slotProps={{ htmlInput: { [DIALOG_AUTO_FOCUS_ATTRIBUTE]: true } }}
            value={values.label}
          />
        );
      }}
    </MemoConfirmDialog>
  );
};
